import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { db, doc, updateDoc, getDoc } from '../../firebase/config';
import { Bell, Lock, Globe, Moon, Sun, Shield, CreditCard, LogOut, ChevronRight, MessageCircle, User, Mail, Phone } from 'lucide-react';

const SettingsPage = () => {
  const navigate = useNavigate();
  const { user, userData, logout } = useAuth();
  const [darkMode, setDarkMode] = useState(false);
  const [loading, setLoading] = useState(false);
  const [userSettings, setUserSettings] = useState({
    notifications: true,
    language: 'English (US)',
    darkMode: false
  });
  const [updating, setUpdating] = useState(false);

  // Load user settings from Firebase
  useEffect(() => {
    const loadSettings = async () => {
      if (!user) return;
      try {
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        if (userDoc.exists()) {
          const data = userDoc.data();
          setUserSettings({
            notifications: data.notifications !== undefined ? data.notifications : true,
            language: data.language || 'English (US)',
            darkMode: data.darkMode || false
          });
          setDarkMode(data.darkMode || false);
        }
      } catch (error) {
        console.error('Error loading settings:', error);
      }
    };
    loadSettings();
  }, [user]);

  const handleLogout = async () => {
    setLoading(true);
    try {
      await logout();
      navigate('/');
    } catch (error) {
      console.error('Logout error:', error);
      alert('Failed to sign out. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const updateSetting = async (key, value) => {
    if (!user) return;
    setUpdating(true);
    try {
      await updateDoc(doc(db, 'users', user.uid), {
        [key]: value,
        updatedAt: new Date().toISOString()
      });
      setUserSettings(prev => ({ ...prev, [key]: value }));
    } catch (error) {
      console.error('Error updating setting:', error);
    } finally {
      setUpdating(false);
    }
  };

  const handleToggleDarkMode = async () => {
    const newValue = !darkMode;
    setDarkMode(newValue);
    await updateSetting('darkMode', newValue);
  };

  const settingsSections = [
    {
      title: 'Preferences',
      items: [
        { 
          icon: <Bell size={18} />, 
          label: 'Notifications', 
          description: userSettings.notifications ? 'Enabled' : 'Disabled',
          toggle: true,
          key: 'notifications',
          value: userSettings.notifications
        },
        { 
          icon: <Globe size={18} />, 
          label: 'Language', 
          description: userSettings.language,
          key: 'language'
        },
        { 
          icon: darkMode ? <Sun size={18} /> : <Moon size={18} />, 
          label: 'Dark Mode', 
          description: darkMode ? 'On' : 'Off',
          toggle: true,
          key: 'darkMode',
          value: darkMode,
          onToggle: handleToggleDarkMode
        },
      ]
    },
    {
      title: 'Account',
      items: [
        { 
          icon: <User size={18} />, 
          label: 'Profile Information', 
          description: userData?.name || 'Not set',
          key: 'profile'
        },
        { 
          icon: <Mail size={18} />, 
          label: 'Email', 
          description: userData?.email || 'Not set',
          key: 'email'
        },
        { 
          icon: <Phone size={18} />, 
          label: 'Phone Number', 
          description: userData?.phone || 'Not set',
          key: 'phone'
        },
        { 
          icon: <Lock size={18} />, 
          label: 'Privacy & Security', 
          description: 'Manage your privacy settings',
          key: 'privacy'
        },
        { 
          icon: <CreditCard size={18} />, 
          label: 'Payment Methods', 
          description: 'Manage your payment options',
          key: 'payment'
        },
        { 
          icon: <Shield size={18} />, 
          label: 'Data & Permissions', 
          description: 'Control your data',
          key: 'data'
        },
      ]
    },
    {
      title: 'Support',
      items: [
        { 
          icon: <MessageCircle size={18} />, 
          label: 'Help Center', 
          description: 'Get help and support',
          key: 'help'
        },
        { 
          icon: <Lock size={18} />, 
          label: 'Privacy Policy', 
          description: 'Read our privacy policy',
          key: 'privacy-policy'
        },
        { 
          icon: <Lock size={18} />, 
          label: 'Terms of Service', 
          description: 'Read our terms',
          key: 'terms'
        },
      ]
    }
  ];

  return (
    <div className="max-w-4xl mx-auto pb-20">
      <div className="mb-8">
        <h1 className="text-3xl font-serif font-bold text-gray-900">Settings</h1>
        <p className="text-gray-500 mt-1">Manage your account preferences</p>
      </div>

      <div className="space-y-6">
        {settingsSections.map((section, index) => (
          <div key={index} className="bg-white rounded-3xl shadow-sm border border-gray-50 overflow-hidden">
            <div className="p-4 border-b border-gray-50">
              <h3 className="text-sm font-bold text-gray-400 uppercase tracking-wider">{section.title}</h3>
            </div>
            <div>
              {section.items.map((item, itemIndex) => (
                <div
                  key={itemIndex}
                  className={`flex items-center justify-between p-4 hover:bg-gray-50 transition-colors ${
                    itemIndex < section.items.length - 1 ? 'border-b border-gray-50' : ''
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-[#FCF8F9] rounded-full flex items-center justify-center text-gray-600 shrink-0">
                      {item.icon}
                    </div>
                    <div>
                      <p className="font-medium text-gray-900 text-sm">{item.label}</p>
                      <p className="text-xs text-gray-400">{item.description}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {item.toggle ? (
                      <button
                        onClick={item.onToggle || (() => updateSetting(item.key, !item.value))}
                        disabled={updating}
                        className={`w-12 h-6 rounded-full transition-colors ${
                          item.value ? 'bg-[#EFA9B6]' : 'bg-gray-300'
                        }`}
                      >
                        <div className={`w-5 h-5 rounded-full bg-white transition-transform ${
                          item.value ? 'translate-x-6' : 'translate-x-0.5'
                        } mt-0.5`} />
                      </button>
                    ) : (
                      <ChevronRight size={18} className="text-gray-400" />
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}

        {/* Sign Out Button */}
        <button
          onClick={handleLogout}
          disabled={loading}
          className="w-full p-4 bg-red-50 text-red-500 rounded-3xl font-bold text-sm hover:bg-red-100 transition-colors flex items-center justify-center gap-2"
        >
          <LogOut size={18} />
          {loading ? 'Signing out...' : 'Sign Out'}
        </button>
      </div>
    </div>
  );
};

export default SettingsPage;